import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import { Flex } from 'antd';

import {
  useAppSelector,
  useNavigationToListRequests,
  useRequisitionInfoExistingReq,
} from '@Hooks/index';
import { createdUserRequestSelector } from '@Store/selectors/userInfoSelector';
import {
  ITRequestFormResponse,
  FormApproveRejectRequest,
} from '@Types/api/form';
import { FooterActions } from '@Enum/index';
import {
  getITAccessories,
  getOrganizationsDict,
  getSIMCapValueDictSelector,
} from '@Store/selectors/dictionarySelector';
import FormItems from '@Components/MainForm/components/FormItems/FormItems';
import { MainForm } from '@Components/index';
import { NotificationContext } from '@Context/NotificationContext';
import { getItRequisitionTypeOptions } from '@Features/EmployeeSelfService/requests/ITRequisition/store/selectors/itRequisitionSelector';
import {
  useGetExistingPackageMutation,
  useRespondITRequestMutation,
} from '@Features/EmployeeSelfService/requests/ITRequisition/store/api/itRequisitionApiSlice';
import ApproveList from '@Components/MainForm/components/ApproveList/ApproveList';
import { getDocument } from '@Features/utils/document';
import { useFormListMutation } from '@Store/api/dmsApiSlice';
import { ReassignWindow } from '@Components/ReassignWindow';
import { EMPTY_FIELD_PLACEHOLDER } from '@Constants';
import { ItRequisitionTypeCodes } from '@Enum/api/ItRequisitionTypeCodes.enum';
import { ItRequisitionStatusCodes } from '@Enum/api/ItRequisitionStatusCodes';

import {
  transformDictToOptions,
  transformDictToOptionsNum,
  transformProjectsToOptions,
} from '@Utils/transformToOptions';
import { useItRequisitionAprroveMutation } from '@Store/api/approvalsListApiSlice';
import { useGetProjectsForOrgMutation } from '@Store/api/dictionaryApiSlice';
import { Option } from '@Components/types/options';
import { initialValuesForm } from '../initialValues';
import { IsNewUser } from '../type';
import { parseSpecificFields } from '../utils';
import { prepareFormHeaderData } from '../utils/prepareFormHeaderData';
import { getDocumentFolder } from '../utils/document';
import { useDataSource } from '../hooks';
import {
  BSAFields,
  EmailCreationFields,
  SimAddonFields,
  SimCardFields,
} from './components';

interface ITRequestApproveFormProps {
  formData: ITRequestFormResponse;
  taskId: string;
}

const ITRequestApproveForm: React.FC<ITRequestApproveFormProps> = ({
  formData,
  taskId,
}) => {
  const {
    errorNotification,
    taskApprovedNotification,
    taskErrorNotification,
    taskRejectedNotification,
  } = useContext(NotificationContext);
  const { handleBackToListRequests } = useNavigationToListRequests();
  const [getExistingPackage] = useGetExistingPackageMutation();
  const [itReqApproveRejectForm] = useItRequisitionAprroveMutation();
  const [getProjects] = useGetProjectsForOrgMutation();
  const [projects, setProjects] = useState<Option[]>([]);
  const requestedUser = useAppSelector(createdUserRequestSelector);
  const itReqTypeOptions = useAppSelector(getItRequisitionTypeOptions);
  const simCapValues = useAppSelector(getSIMCapValueDictSelector);
  const organizations = useAppSelector(getOrganizationsDict);
  const itAccessories = useAppSelector(getITAccessories);

  const [postApproveRejectForm] = useRespondITRequestMutation();
  const [getFormList] = useFormListMutation();

  const [isReassignOpen, setReassignOpen] = useState(false);
  const [existingPackageValue, setExistingPackageValue] = useState('');
  const [comments, setCommentValue] = useState('');
  const [documentId, setDocumentId] = useState('');
  const [bsaValues, setBSAValues] = useState({
    password: '',
    username: '',
  });

  const [emailCreationValues, setEmailCreationValues] = useState({
    password: '',
    username: '',
  });

  const [simCardValues, setSimCardValues] = useState({
    reqCapValue: formData?.reqCapValue,
    mobileNumber: formData?.personalMobile,
  });

  const [simAddonValues, setSimAddonValues] = useState({
    recommendPackage: '',
  });

  const formHeaderData = useMemo(() => {
    const output = prepareFormHeaderData(formData);
    return output;
  }, [formData]);

  const requisitionInfo = useRequisitionInfoExistingReq(
    formHeaderData,
    'ITRequisition'
  );

  const initialFromFormData = useMemo(() => {
    if (requestedUser && formData) {
      const restFields = parseSpecificFields(
        formData.reqItemId,
        formData.personalMobile,
        formData.username,
        formData.accessories
      );

      return {
        initialValuesForm,
        itReqType: formData.reqItemId,
        simCardCreditLimit: formData.reqCapValue,
        document: formData.fileName,
        isNewUser: formData.isNewUser as IsNewUser,
        module: formData.modules || EMPTY_FIELD_PLACEHOLDER,
        accountsModule: formData.acctModules || EMPTY_FIELD_PLACEHOLDER,
        organization: formData.organizations[0],
        projects: formData.projects || EMPTY_FIELD_PLACEHOLDER,
        requiredRole: formData.role,
        remarks: formData.comments || EMPTY_FIELD_PLACEHOLDER,
        recommendPackage: formData.recommendPackage || EMPTY_FIELD_PLACEHOLDER,
        existingPackage: formData.existingPackage || EMPTY_FIELD_PLACEHOLDER,
        ...restFields,
      };
    }
    return initialValuesForm;
  }, [formData, requestedUser]);

  const formik = useFormik({
    initialValues: initialFromFormData,
    enableReinitialize: true,
    onSubmit: (): void => {},
  });

  useEffect(() => {
    if (formData?.requestId) {
      getExistingPackage(formData.requestId)
        .unwrap()
        .then((response) => setExistingPackageValue(response?.packages || ''));
    }
  }, [formData.requestId, getExistingPackage]);

  useEffect(() => {
    if (formData && formData.organizations?.length) {
      getProjects(formData.organizations.join(''))
        .unwrap()
        .then((projectsResp) => {
          const transformedProjects = transformProjectsToOptions(projectsResp);
          setProjects(transformedProjects);
        })
        .catch(() => {
          setProjects([]);
        });
    }
  }, [formData, getProjects]);

  useEffect(() => {
    if (!formData?.fileName || !formData?.requestId) {
      return;
    }

    const documentFolderPath = getDocumentFolder(formData.requestId);

    getFormList(documentFolderPath)
      .unwrap()
      .then((response) => {
        const matchedDocument = getDocument(response, formData.fileName);

        if (matchedDocument) {
          setDocumentId(matchedDocument.documentId);
        }
      });
  }, [formData, getFormList]);

  useEffect(() => {
    if (formData) {
      setEmailCreationValues({
        username: formData.username || '',
        password: formData.password,
      });
    }
  }, [formData]);

  const isNewEmailRequest = useMemo(() => {
    if (
      formData?.reqItemId === ItRequisitionTypeCodes.EMAIL &&
      formData?.statusId ===
        ItRequisitionStatusCodes.AWAITING_FOR_EMAIL_CREATION
    ) {
      return true;
    }

    return false;
  }, [formData?.reqItemId, formData?.statusId]);

  const isSimCardRequest = useMemo(() => {
    if (formData?.reqItemId === ItRequisitionTypeCodes.NEW_SIM_CARD) {
      if (
        formData?.statusId ===
        ItRequisitionStatusCodes.AWAITING_SIM_CARD_FROM_ETISALAT
      ) {
        return 'Input';
      }

      if (
        formData?.statusId ===
          ItRequisitionStatusCodes.AWAITING_APPROVAL_OF_ADMIN_MANAGER ||
        formData?.statusId ===
          ItRequisitionStatusCodes.AWAITING_APPROVAL_OF_PAYROLL_ADMIN
      ) {
        return 'Dropdown';
      }
    }
    return '';
  }, [formData?.reqItemId, formData?.statusId]);

  const isSimAddonRequest = useMemo(() => {
    if (
      formData?.reqItemId === ItRequisitionTypeCodes.SIM_ADDON_REQUEST &&
      formData?.statusId === ItRequisitionStatusCodes.AWAITING_APPROVAL &&
      !formData?.recommendPackage
    ) {
      return true;
    }

    return false;
  }, [formData?.recommendPackage, formData?.reqItemId, formData?.statusId]);

  const isBSARequest = useMemo(() => {
    if (
      formData?.reqItemId === ItRequisitionTypeCodes.BUILD_SMART_ACCESS &&
      formData?.statusId === ItRequisitionStatusCodes.UNDER_PROCESS
    ) {
      return true;
    }

    return false;
  }, [formData?.statusId, formData?.reqItemId]);

  const handleApproveReject = useCallback(
    (action: FooterActions.APPROVE | FooterActions.REJECT): void => {
      let body: FormApproveRejectRequest = {
        taskId: String(taskId),
        command: action,
        comment: comments,
      };

      if (action === FooterActions.REJECT && !comments) {
        errorNotification('Please leave a comment');
        return;
      }

      let requestCb;

      if (isBSARequest) {
        const { username, password } = bsaValues;
        const bodyBSA = {
          taskId: String(taskId),
          command: action,
          comment: comments,
          userName: username,
          password,
        };

        requestCb = itReqApproveRejectForm(bodyBSA);
      }

      if (isSimAddonRequest) {
        const { recommendPackage } = simAddonValues;
        const bodySimAddon = {
          taskId: String(taskId),
          command: action,
          comment: comments,
          existingPackage: existingPackageValue,
          recommendPackage,
        };

        requestCb = itReqApproveRejectForm(bodySimAddon);
      }

      if (isSimCardRequest) {
        body = { ...body, ...simCardValues };
      }

      if (isNewEmailRequest) {
        const { username, password } = emailCreationValues;
        if (username.includes('@') && password.length > 4) {
          const bodyNewEmail = { ...body, userName: username, password };

          requestCb = itReqApproveRejectForm(bodyNewEmail);
        }

        if (!username.includes('@')) {
          errorNotification(
            'Email ID must contains @ (at the rate), Kindly verify!'
          );
          return;
        }

        if (!username.length || !password.length) {
          errorNotification('Username or Password must not be empty!');
          return;
        }
      }

      // fallback
      if (!requestCb) {
        requestCb = postApproveRejectForm(body);
      }

      requestCb
        .unwrap()
        .then(() => {
          if (action === FooterActions.APPROVE) {
            taskApprovedNotification();
          } else {
            taskRejectedNotification();
          }

          handleBackToListRequests();
        })
        .catch(() => {
          taskErrorNotification();
        });
    },
    [
      taskId,
      comments,
      postApproveRejectForm,
      isBSARequest,
      isSimAddonRequest,
      isSimCardRequest,
      isNewEmailRequest,
      errorNotification,
      bsaValues,
      itReqApproveRejectForm,
      simAddonValues,
      existingPackageValue,
      simCardValues,
      emailCreationValues,
      handleBackToListRequests,
      taskApprovedNotification,
      taskRejectedNotification,
      taskErrorNotification,
    ]
  );

  const handleFooterAction = useCallback(
    (action: FooterActions, payload?: string) => {
      if (action === FooterActions.APPROVE || action === FooterActions.REJECT) {
        handleApproveReject(action);
      }

      if (action === FooterActions.COMMENTS_CHANGE) {
        setCommentValue(payload || '');
      }

      if (action === FooterActions.BACK_TO_REQUESTS) {
        handleBackToListRequests();
      }

      if (action === FooterActions.REASSIGN) {
        setReassignOpen(true);
      }
    },
    [handleBackToListRequests, handleApproveReject]
  );

  const dataSource = useDataSource(
    formData,
    formik.values,
    documentId,
    itReqTypeOptions,
    simCapValues,
    organizations,
    projects,
    transformDictToOptions(itAccessories)
  );

  return (
    <Flex gap={10} vertical style={{ height: '100%' }}>
      <MainForm
        handleSubmit={formik.handleSubmit}
        userInfo={requestedUser}
        requisitionInfo={requisitionInfo}
        onFooterAction={handleFooterAction}
        formType="approval"
      >
        <FormItems>
          <ApproveList dataSource={dataSource} label="Request Details" />
        </FormItems>
        {isNewEmailRequest && (
          <EmailCreationFields
            username={emailCreationValues.username}
            setValues={setEmailCreationValues}
          />
        )}
        {isSimCardRequest && (
          <SimCardFields
            simCardRequest={isSimCardRequest}
            setValues={setSimCardValues}
            selectedValue={simCardValues.reqCapValue}
            simCapValues={transformDictToOptionsNum(simCapValues)}
          />
        )}
        {isBSARequest && <BSAFields setValues={setBSAValues} />}
        {isSimAddonRequest && (
          <SimAddonFields
            existingPackage={existingPackageValue}
            setValues={setSimAddonValues}
          />
        )}
      </MainForm>
      <ReassignWindow
        creatorRequestNumber={requestedUser?.empNumber}
        taskId={taskId}
        isOpen={isReassignOpen}
        onClose={() => {
          setReassignOpen(false);
        }}
        onUserSelected={() => {
          setReassignOpen(false);
          handleBackToListRequests();
        }}
      />
    </Flex>
  );
};

export default ITRequestApproveForm;
